<template>
  <div>
    <b-alert
      v-if="!isFormValid"
      variant="danger"
      class="mb-3"
      show
      dismissible
    >
      You have to set a start date and end date before publishing the campaign.
    </b-alert>
    <advanced-form
      autocomplete="off"
      layout="inline-md-3"
      :submit-label="$t('content-fetch.buttons.update-campaign')"
      v-bind="formFields"
      v-model="form"
      @submit="input()"
    />
  </div>
</template>

<script>
import AdvancedForm from '@src/components/AdvancedForm';
import UpdateCampaignMutation from '@/graphql/mutations/UpdateCampaign.gql';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    campaign: {
      type: Object,
    },
  },
  components: {
    AdvancedForm,
  },
  data: () => ({
    form: {},
  }),
  created() {
    const { campaign: { title, status, startDate, endDate, preferences } } = this;
    const {
      isGlobalTemplate,
      canScheduleLinkedInPosts,
      showRecruitersOnLeaderboards,
      hasRelativeDates,
      isTemplate,
    } = preferences || {};
    this.form = {
      title,
      status,
      startDate,
      endDate,
      'preferences.canScheduleLinkedInPosts': !!canScheduleLinkedInPosts,
      'preferences.showRecruitersOnLeaderboards': !!showRecruitersOnLeaderboards,
      'preferences.hasRelativeDates': !!hasRelativeDates,
      'preferences.isTemplate': !!isTemplate,
      'preferences.isGlobalTemplate': !!isGlobalTemplate,
    };
  },
  computed: {
    ...mapRoot([ 'company' ]),
    sitePreferences() {
      const { sitePreferences } = this.company || {};
      return sitePreferences || {};
    },
    formFields() {
      const { sitePreferences } = this;
      let { featuresEnabled } = sitePreferences || {};
      featuresEnabled = Array.isArray(featuresEnabled) ? featuresEnabled : [];

      const statusChoices = {
        DRAFT: this.$t('campaign-admin.labels.draft'),
        IN_PROGRESS: this.$t('campaign-admin.labels.in-progress'),
        ARCHIVED: this.$t('campaign-admin.labels.archived'),
      };

      if (this.campaign.status === 'READY') {
        statusChoices.READY = this.$t('campaign-admin.labels.ready');
      }

      if (this.campaign.status === 'FINISHED') {
        statusChoices.FINISHED = this.$t('campaign-admin.labels.finished');
      }

      const form = {
        title: {
          label: this.$t('content-fetch.labels.title'),
          type: 'STRING',
          helpText: this.$t('campaign-admin.hints.title')
        },
        startDate: {
          label: this.$t('content-fetch.labels.start-date'),
          placeholder: this.$t('campaign-admin.placeholders.end-date'),
          dateRange: { startDate: new Date() },
          helpText: this.$t('campaign-admin.hints.start-date'),
          required: true,
          type: 'DATETIME',
        },
        endDate: {
          label: this.$t('content-fetch.labels.end-date'),
          placeholder: this.$t('campaign-admin.placeholders.start-date'),
          helpText: this.$t('campaign-admin.hints.end-date'),
          type: 'DATETIME',
        },
        status: {
          label: this.$t('onboarding.slack.labels.status'),
          type: 'SELECT',
          required: true,
          choices: statusChoices,
        },
        'preferences.canScheduleLinkedInPosts': {
          description: this.$t('campaign-admin.labels.schedule-linked-in'),
          type: 'CHECKBOX',
        },
        ...(featuresEnabled.includes('can-create-global-templates') ? {
          'preferences.isGlobalTemplate': {
            description: 'Use this campaign as a global template',
            type: 'CHECKBOX',
          },
        } : {}),
        'preferences.showRecruitersOnLeaderboards': {
          description: this.$t('campaign-admin.labels.show-recruiters'),
          type: 'CHECKBOX',
        },
        ...(featuresEnabled.includes('relative-message-dates') ? {
          'preferences.hasRelativeDates': {
            description: 'Use relative dates for messages',
            type: 'CHECKBOX',
          },
        } : {}),
        ...(featuresEnabled.includes('relative-message-dates') ? {
          'preferences.isTemplate': {
            description: 'Use this campaign as a template for future campaigns',
            type: 'CHECKBOX',
          },
        } : {}),
      };

      return { form };
    },
    isFormValid() {
      const { startDate, endDate } = this.form;
      return startDate && endDate;
    },
  },
  methods: {
    async input() {
      const { isFormValid } = this;
      if (!isFormValid) return;
      try {
        const { id: campaignID, preferences } = this.campaign;
        const { title, status, startDate, endDate } = this.form;
        await this.$apollo.mutate({
          mutation: UpdateCampaignMutation,
          variables: {
            where: { id: campaignID },
            update: {
              title,
              status,
              startDate,
              endDate,
              preferences: {
                ...(preferences || {}),
                canScheduleLinkedInPosts: !!this.form['preferences.canScheduleLinkedInPosts'],
                showRecruitersOnLeaderboards: !!this.form['preferences.showRecruitersOnLeaderboards'],
                hasRelativeDates: !!this.form['preferences.hasRelativeDates'],
                isTemplate: !!this.form['preferences.isTemplate'],
                isGlobalTemplate: !!this.form['preferences.isGlobalTemplate'],
              },
            },
          },
        });
        this.$emit('updated');
      } catch (err) {
        this.$log.error(err);
        this.$notification({
          title: 'An error occurred',
          message: err.message,
          variant: 'danger',
        });
      }
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-campaign-details.vue